import React from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';

import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { isHasAccess } from 'features/validateAccess';
import { ProjectStatus, projectsService } from 'entities/project';
import { withSuspense } from 'shared/lib/react';
import { EmptyGroup } from 'shared/ui/EmptyGroup';
import { Loader } from 'shared/ui/Loader';
import { RcPagination } from 'shared/ui/RcPagination';

import { PAGE_SIZE, DEFAULT_SORT_ORDER } from '../../ProjectList.const';
import {
  getEditAllProjectPermissions,
  getEditProjectLocalPermission,
  getGlobalReadPositionPermission,
  getLocalReadPositionPermission,
} from '../../ProjectList.permissions';
import { ProjectItem } from '../ProjectItem';

import styles from './ProjectList.module.scss';

const ProjectList = ({
  page,
  setPage,
  status,
}: {
  page: number;
  setPage: (page: number) => void;
  status: ProjectStatus;
}): React.ReactElement => {
  const { accountId = '' } = useParams();

  const { t } = useTranslation('Project');

  const canEditAllProject = isHasAccess(getEditAllProjectPermissions(accountId));
  const canSeeAllPositions = isHasAccess(getGlobalReadPositionPermission(accountId));

  const {
    data: { content: projectList, ...projectListAuxData },
  } = useSuspenseQuery(
    projectsService.queryOptions({
      accountId,
      page: page - 1,
      size: PAGE_SIZE,
      filter: status,
      sort: DEFAULT_SORT_ORDER,
    }),
  );

  return (
    <React.Fragment>
      <ul className={styles.list} data-testid="data-list">
        <li className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.namePlaceholder}>&nbsp;</div>
            <div className={styles.status}>{t('ProjectList.status')}</div>
            <div className={styles.positionsCount}>{t('ProjectList.activePositions')}</div>
            <div className={styles.buttonPlacehoder}>&nbsp;</div>
          </div>
        </li>
        {projectList.length > 0 ? (
          projectList.map((project) => {
            const canEditProject = canEditAllProject || isHasAccess(getEditProjectLocalPermission(project.id));
            const canSeePositions = canSeeAllPositions || isHasAccess(getLocalReadPositionPermission(project.id));

            return (
              <ProjectItem
                key={project.id}
                accountId={project.accountId}
                projectId={project.id}
                disableLink={!canSeePositions}
                isActive={project.active}
                name={project.name}
                positionCount={project.positionsCount}
                hideEditButton={!canEditProject}
                archived={project.archived}
              />
            );
          })
        ) : (
          <EmptyGroup />
        )}
      </ul>
      <div className={styles.pagination}>
        <RcPagination
          pageSize={PAGE_SIZE}
          setCurrentPage={(pageNum) => {
            setPage(pageNum);

            setTimeout(() => {
              document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' });
            }, 0);
          }}
          dataSize={projectListAuxData?.totalElements ?? 0}
          current={page}
        />
      </div>
    </React.Fragment>
  );
};
export default withSuspense(ProjectList, { fallback: <Loader /> });
